import {Form, Formik} from 'formik'
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import TextInput from '../../widgets/components/Input/TextInput'
import * as Yup from 'yup'
import {CustomButton} from '../../widgets/components/UI/CustomButton'
import {useAuth} from '../../auth'
import {toast} from 'react-toastify'
import {SelectInput} from '../../widgets/components/Input/SelectInput'
import {operatingStatusOptions} from '../../profile/core/_constants'
import {updateCompanyProfile} from '../../profile/core/_requests'
import {verifyToken} from '../../auth/core/_requests'
import {Spinner} from '../../widgets/components/General/Spinner'
import Select from 'react-select'
import {ToolTipUI} from '../../widgets/components/UI/ToolTipUI'
import {getCountry} from '../../onboarding/core/_requests'
import {CountryModel} from '../../onboarding'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'
import { removeNullFields } from '../../../core/_util'

export const AddBasicDetailsModal = ({
  showBasicDetailsModal,
  setShowBasicDetailsModal,
  getCompanyPageData,
  companyPageData,
  companyApiLoading,
}: {
  showBasicDetailsModal: boolean
  setShowBasicDetailsModal: Dispatch<SetStateAction<boolean>>
  getCompanyPageData: any
  companyPageData: any
  companyApiLoading: boolean
}) => {
  const {formatMessage} = useIntl()
  const [loading, setLoading] = useState(false)
  const {companyId, currentUser, setCurrentUser} = useAuth()
  const [selectedValue, setSelectedValue] = useState<{label: string; value: number}[]>([])
  const [countryOptions, setcountryOptions] = useState<any[]>([])

  const companyInitialValues = {
    foundedDate: '',
    founders: '',
    operatingStatus: '',
    legalName: '',
    lastFundingType: '',
    operatingCountries: '',
  }

  const companySchema = Yup.object().shape({
    foundedDate: Yup.date()
      .required(formatMessage({id: 'Founded Date is required'}))
      .nullable(),
    founders: Yup.string()
      .required(formatMessage({id: 'Founders is required'}))
      .nullable(),
    operatingStatus: Yup.string()
      .required(formatMessage({id: 'Operating status is required'}))
      .nullable(),
    legalName: Yup.string()
      .required(formatMessage({id: 'Legal Name is required'}))
      .min(3, formatMessage({id: 'Minimum 3 characters'}))
      .nullable(),
    lastFundingType: Yup.string().nullable(),
    operatingCountries: Yup.string().nullable(),
  })

  console.log(companyPageData, ' companyPageData')

  const onSubmit = async (values: any) => {
    const selectedCountries = selectedValue.map((item) => item.label).join(', ')
    const updateCompanyData = {
      ...values,
      name: companyPageData.name,
      teamSize: companyPageData.teamSize,
      tagline: companyPageData.tagline,
      industry: companyPageData.industry,
      operatingCountries: selectedCountries,
      companyId: companyId,
    }
    try {
      setLoading(true)
      const {
        data: {success, errors, data},
      } = await updateCompanyProfile(updateCompanyData)
      if (success) {
        setLoading(false)
        getCompanyPageData()
        setShowBasicDetailsModal(false)
        toast.success(formatMessage({id: 'Company updated successfully'}))
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.UpdateCompanyProfileSuccessful,
            action: 'normal',
            metadata: removeNullFields({
              name: companyPageData.name,
              team_size: companyPageData.teamSize,
              tagline: companyPageData.tagline,
              industry: companyPageData.industry,
              operating_countries: selectedCountries,
              company_id: companyId,
            }),
          },
        })
        const {
          data: {success},
        } = await verifyToken(data.token)
        if (success) {
          setCurrentUser(currentUser)
        }
      } else {
        setLoading(false)
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.UpdateCompanyProfileFailed,
            action: 'normal',
            metadata: removeNullFields({
              name: companyPageData.name,
              team_size: companyPageData.teamSize,
              tagline: companyPageData.tagline,
              industry: companyPageData.industry,
              operating_countries: selectedCountries,
              company_id: companyId,
              error: errors[0],
            }),
          },
        })
      }
    } catch (err) {
      setLoading(false)
      console.log(err)
    }
  }

  useEffect(() => {
    const fetchCountry = async () => {
      const {
        data: {data: countries},
      } = await getCountry()
      const countriesData = countries.map((country: CountryModel) => {
        return {
          id: country.countryId,
          label: country.country_name,
          value: country.countryId,
        }
      })
      setcountryOptions([...countriesData])
    }
    fetchCountry()
  }, [])

  useEffect(() => {
    if (countryOptions && companyPageData?.operatingCountries) {
      const countries = companyPageData?.operatingCountries?.split(', ')
      const result = countryOptions.filter((item) => countries.includes(item.label))
      setSelectedValue(result)
    }
  }, [countryOptions, companyPageData?.operatingCountries])

  const handleChangeCountries = (selectedOptions: any) => {
    setSelectedValue(selectedOptions)
  }

  return (
    <Modal
      size='lg'
      show={showBasicDetailsModal}
      onHide={() => setShowBasicDetailsModal(false)}
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      {companyApiLoading ? <Spinner /> : null}
      <Formik
        initialValues={companyInitialValues}
        validationSchema={companySchema}
        onSubmit={onSubmit}
      >
        {(formik) => {
          formik.initialValues.foundedDate = companyPageData?.foundedDate?.split('T')[0]
          formik.initialValues.founders = companyPageData?.founders
          formik.initialValues.operatingStatus = companyPageData?.operatingStatus
          formik.initialValues.legalName = companyPageData?.legalName
          formik.initialValues.lastFundingType = companyPageData?.lastFundingType
          formik.initialValues.operatingCountries = companyPageData?.operatingCountries

          return (
            <Form>
              <Modal.Body>
                <div className='d-flex justify-content-between m-5'>
                  <h2>{formatMessage({id: 'Edit Company Details'})}</h2>
                  <img
                    src={toAbsoluteUrl('/media/icons/investor/Cancel.svg')}
                    alt='cancel'
                    className='cursor-pointer'
                    onClick={() => {
                      setShowBasicDetailsModal(false)
                    }}
                  />
                </div>
                <div className='mt-8 m-5'>
                  <div className='row d-md-flex justify-content-between w-100'>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'date'}
                        fieldName={'foundedDate'}
                        formik={formik}
                        placeholder={formatMessage({
                          id: 'Select the Founding Date',
                        })}
                        margin='me-6'
                        label={formatMessage({id: 'Founding Date'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_BASIC_DETAILS_MODAL.FOUNDING_DATE',
                        })}
                        dateValue={formik.values.foundedDate}
                        isStarRequired={true}
                      />
                    </div>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'text'}
                        fieldName={'founders'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter the Founders'})}
                        label={formatMessage({id: 'Founders'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_BASIC_DETAILS_MODAL.FOUNDERS',
                        })}
                        isTooltipNotRequired={false}
                        isStarRequired={true}
                      />
                    </div>
                  </div>
                  <div className='row d-md-flex justify-content-between w-100'>
                    <div className='col-md-6'>
                      <SelectInput
                        label={formatMessage({id: 'Operating Status'})}
                        fieldName={'operatingStatus'}
                        placeholder={formatMessage({
                          id: 'Select your Operating Status',
                        })}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_BASIC_DETAILS_MODAL.OPERATING_STATUS',
                        })}
                        options={operatingStatusOptions}
                        margin='me-6'
                      />
                    </div>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'text'}
                        label={formatMessage({id: 'Legal Name'})}
                        fieldName={'legalName'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter the Legal Name'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_BASIC_DETAILS_MODAL.LEGAL_NAME',
                        })}
                        isStarRequired={true}
                      />
                    </div>
                  </div>
                  <div className='row d-md-flex justify-content-between w-100'>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'text'}
                        label={formatMessage({id: 'Last Funding Type'})}
                        fieldName={'lastFundingType'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter Last Funding Type'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_BASIC_DETAILS_MODAL.LAST_FUNDING_TYPE',
                        })}
                        isStarRequired={false}
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                        {formatMessage({id: 'Operating Countries'})}
                        <ToolTipUI
                          tooltipText={formatMessage({
                            id: 'GLOBAL.TOOLTIP.ADD_BASIC_DETAILS_MODAL.OPERATING_COUNTRIES',
                          })}
                        />
                      </label>

                      <Select
                        classNamePrefix='react-select'
                        value={selectedValue}
                        closeMenuOnSelect={false}
                        isMulti
                        name='operatingCountries'
                        options={countryOptions}
                        onChange={handleChangeCountries}
                      />
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className='p-0'>
                <button
                  type='button'
                  className='btn btn-light font-size-13 font-weight-400'
                  onClick={() => {
                    setShowBasicDetailsModal(false)
                  }}
                >
                  {formatMessage({id: 'Cancel'})}
                </button>
                <CustomButton
                  isSubmitting={formik.isSubmitting}
                  isValid={formik.isValid}
                  buttonText={formatMessage({
                    id: 'Edit Company Details',
                  })}
                  loading={loading}
                  widthLoading={2}
                  width={2}
                  margin={'me-5 mt-5'}
                />
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
